import { ArrowDown, ArrowUp, ArrowUpDown, PencilLine, Plus, Trash2 } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

const statusClasses = {
  Active: "bg-emerald-50 text-emerald-700 border-emerald-200",
  Trial: "bg-amber-50 text-amber-700 border-amber-200",
  Cancelled: "bg-rose-50 text-rose-700 border-rose-200",
  "Past Due": "bg-orange-50 text-orange-700 border-orange-200",
};

const roleClasses = {
  admin: "bg-[#001935]/6 text-[#001935] border-[#001935]/12",
  manager: "bg-[#D4AF37]/12 text-[#8b6d16] border-[#D4AF37]/30",
  agent: "bg-sky-50 text-sky-700 border-sky-200",
};

const getInitials = (name = "") =>
  name
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase())
    .join("") || "U";

const getWhatsAppHref = (phone, name) => {
  const digits = String(phone || "").replace(/\D/g, "");
  if (!digits) return "#";
  return `https://wa.me/${digits}?text=${encodeURIComponent(`Hello ${name || "there"}, I would like to discuss your portal.`)}`;
};

export const AdminUsersCommandCenter = ({
  filteredUsers,
  formatDate,
  onCreateUser,
  onDeleteUser,
  onEditUser,
  onSortChange,
  onSortHeaderClick,
  planOptions,
  planFilter,
  searchValue,
  setPlanFilter,
  setSearchValue,
  sortState,
}) => {
  const sortValue = `${sortState.key}:${sortState.direction}`;
  const stickyHeaderTopClass = "top-[152px] xl:top-[92px]";

  const renderSortIcon = (key) => {
    if (sortState.key !== key) return <ArrowUpDown className="h-3.5 w-3.5" />;
    return sortState.direction === "asc" ? <ArrowUp className="h-3.5 w-3.5" /> : <ArrowDown className="h-3.5 w-3.5" />;
  };

  const SortableHead = ({ label, sortKey, testId }) => (
    <TableHead className={`sticky ${stickyHeaderTopClass} z-20 bg-[#FDFBF7] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5e6c7b]`}>
      <button className="inline-flex items-center gap-2 transition hover:text-[#001935]" data-testid={testId} onClick={() => onSortHeaderClick(sortKey)} type="button">
        <span>{label}</span>
        {renderSortIcon(sortKey)}
      </button>
    </TableHead>
  );

  return (
    <div className="rounded-[28px] border border-[#001935]/10 bg-white/95 p-6 shadow-[0_20px_60px_rgba(0,25,53,0.08)]" data-testid="user-management-list">
      <div className="sticky top-4 z-30 -mx-6 border-b border-[#001935]/10 bg-white/95 px-6 pb-4 pt-1 backdrop-blur-sm" data-testid="users-command-bar-sticky">
        <div className="flex flex-col gap-4 xl:flex-row xl:items-end xl:justify-between">
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#8b6d16]">Users Command Center</p>
            <h2 className="mt-2 text-2xl font-semibold tracking-tight text-[#001935]">Existing users</h2>
          </div>

          <div className="flex w-full flex-col gap-3 xl:w-auto xl:flex-row xl:items-center">
            <div className="grid w-full gap-3 sm:grid-cols-[minmax(260px,1fr)_180px_180px] xl:w-[720px]">
              <input
                className="h-10 w-full rounded-sm border border-[#001935]/20 bg-white px-3 py-2 text-sm text-[#001935] shadow-sm outline-none placeholder:text-[#5e6c7b] focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
                data-testid="user-search-input"
                onChange={(event) => setSearchValue(event.target.value)}
                placeholder="Search by name or email"
                value={searchValue}
              />
              <select
                className="h-10 w-full rounded-sm border border-[#001935]/20 bg-white px-3 py-2 text-sm text-[#001935] shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
                data-testid="user-plan-filter-select"
                onChange={(event) => setPlanFilter(event.target.value)}
                value={planFilter}
              >
                <option value="all">All plans</option>
                {planOptions.map((plan) => <option key={plan} value={plan}>{plan}</option>)}
              </select>
              <select
                className="h-10 w-full rounded-sm border border-[#001935]/20 bg-white px-3 py-2 text-sm text-[#001935] shadow-sm outline-none focus-visible:ring-2 focus-visible:ring-[#D4AF37]"
                data-testid="user-sort-select"
                onChange={(event) => onSortChange(event.target.value)}
                value={sortValue}
              >
                <option value="created_at:desc">Recently created</option>
                <option value="created_at:asc">Oldest created</option>
                <option value="role:asc">Role A–Z</option>
                <option value="role:desc">Role Z–A</option>
                <option value="plan_type:asc">Plan A–Z</option>
                <option value="plan_type:desc">Plan Z–A</option>
                <option value="subscription_status:asc">Status A–Z</option>
                <option value="subscription_status:desc">Status Z–A</option>
              </select>
            </div>

            <button
              className="inline-flex h-10 shrink-0 items-center justify-center rounded-sm bg-[#001935] px-4 py-2 text-sm font-medium text-[#FDFBF7] shadow-sm transition hover:bg-[#000d1b]"
              data-testid="user-open-create-drawer-button"
              onClick={onCreateUser}
              type="button"
            >
              <Plus className="mr-2 h-4 w-4" /> Add New Agent
            </button>
          </div>
        </div>
      </div>

      {!filteredUsers.length ? (
        <div className="mt-4 rounded-sm border border-dashed border-[#001935]/15 bg-[#fffaf1] px-4 py-6 text-sm text-[#6b6252]" data-testid="user-management-empty-state">
          No users match the current plan filter and search.
        </div>
      ) : (
        <>
        <div className="mt-4 grid gap-4 md:hidden" data-testid="user-management-mobile-card-list">
          {filteredUsers.map((member) => (
            <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] p-5 shadow-[0_16px_45px_rgba(0,25,53,0.06)]" data-testid={`user-mobile-card-${member.id}`} key={`mobile-${member.id}`}>
              <div className="flex items-start gap-4">
                {member.profile_photo_url ? (
                  <img alt={`${member.name} thumbnail`} className="h-12 w-12 rounded-full object-cover" data-testid={`user-mobile-thumbnail-${member.id}`} loading="lazy" src={member.profile_photo_url} />
                ) : (
                  <div className="flex h-12 w-12 items-center justify-center rounded-full bg-[#001935]/8 text-sm font-semibold text-[#001935]" data-testid={`user-mobile-thumbnail-fallback-${member.id}`}>
                    {getInitials(member.name)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-lg font-semibold text-[#001935]" data-testid={`user-mobile-name-${member.id}`}>{member.name}</p>
                  <p className="mt-1 break-all text-sm text-[#5e6c7b]" data-testid={`user-mobile-email-${member.id}`}>{member.email}</p>
                  <p className="mt-2 text-xs text-[#8b8476]">Created {formatDate(member.created_at)}</p>
                </div>
              </div>

              <div className="mt-4 grid gap-3 sm:grid-cols-2">
                <div className="rounded-[18px] border border-[#001935]/10 bg-white px-4 py-3">
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Phone</p>
                  <p className="mt-2 text-sm font-semibold text-[#001935]">{member.phone || "—"}</p>
                </div>
                <div className="rounded-[18px] border border-[#001935]/10 bg-white px-4 py-3">
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Plan / Status</p>
                  <p className="mt-2 text-sm font-semibold text-[#001935]">{member.plan_type || "—"} · {member.subscription_status || "Trial"}</p>
                </div>
              </div>

              <div className="mt-4 flex flex-wrap gap-2" data-testid={`user-mobile-actions-${member.id}`}>
                <a className="inline-flex items-center justify-center rounded-full bg-[#D4AF37] px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#1A1A1A]" data-testid={`user-mobile-whatsapp-${member.id}`} href={getWhatsAppHref(member.phone, member.name)} rel="noreferrer" target="_blank">
                  Quick-Action WhatsApp
                </a>
                <a className="inline-flex items-center justify-center rounded-full border border-[#001935]/12 bg-white px-4 py-2 text-xs font-bold uppercase tracking-[0.16em] text-[#001935]" data-testid={`user-mobile-email-trigger-${member.id}`} href={`mailto:${member.email}`}>
                  Email Trigger
                </a>
                <button className="inline-flex h-10 items-center justify-center rounded-full border border-[#001935]/15 px-4 text-xs font-bold uppercase tracking-[0.16em] text-[#001935] transition hover:bg-[#001935]/5" data-testid={`user-mobile-edit-button-${member.id}`} onClick={() => onEditUser(member.id)} type="button">
                  Edit User
                </button>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-4 hidden rounded-sm border border-[#001935]/10 md:block">
          <Table className="min-w-full border-collapse" containerClassName="overflow-visible">
            <TableHeader className="bg-[#FDFBF7]">
              <TableRow className="border-b border-[#001935]/10 hover:bg-transparent">
                <TableHead className={`sticky ${stickyHeaderTopClass} z-20 bg-[#FDFBF7] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5e6c7b]`}>Photo</TableHead>
                <SortableHead label="Name / Email" sortKey="created_at" testId="user-sort-header-created-at" />
                <TableHead className={`sticky ${stickyHeaderTopClass} z-20 bg-[#FDFBF7] px-3 py-2 text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5e6c7b]`}>Phone</TableHead>
                <SortableHead label="Role" sortKey="role" testId="user-sort-header-role" />
                <SortableHead label="Plan" sortKey="plan_type" testId="user-sort-header-plan" />
                <SortableHead label="Status" sortKey="subscription_status" testId="user-sort-header-status" />
                <TableHead className={`sticky ${stickyHeaderTopClass} z-20 bg-[#FDFBF7] px-3 py-2 text-right text-[11px] font-semibold uppercase tracking-[0.18em] text-[#5e6c7b]`}>Actions</TableHead>
              </TableRow>
            </TableHeader>

            <TableBody>
              {filteredUsers.map((member) => (
                <TableRow className="border-b border-[#001935]/10 hover:bg-[#001935]/[0.02]" data-testid={`user-row-${member.id}`} key={member.id}>
                  <TableCell className="px-3 py-2">
                    {member.profile_photo_url ? (
                      <img alt={`${member.name} thumbnail`} className="h-8 w-8 rounded-sm object-cover" data-testid={`user-thumbnail-${member.id}`} src={member.profile_photo_url} />
                    ) : (
                      <div className="flex h-8 w-8 items-center justify-center rounded-sm bg-[#001935]/8 text-[11px] font-semibold text-[#001935]" data-testid={`user-thumbnail-fallback-${member.id}`}>
                        {getInitials(member.name)}
                      </div>
                    )}
                  </TableCell>
                  <TableCell className="px-3 py-2">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-[#001935]">{member.name}</p>
                      <p className="truncate text-xs text-[#5e6c7b]">{member.email}</p>
                      <p className="truncate text-[11px] text-[#8b8476]">Created {formatDate(member.created_at)}</p>
                    </div>
                  </TableCell>
                  <TableCell className="px-3 py-2 text-sm text-[#001935]">{member.phone || "—"}</TableCell>
                  <TableCell className="px-3 py-2">
                    <span className={`inline-flex rounded-sm border px-2 py-1 text-xs font-medium capitalize ${roleClasses[member.role] || roleClasses.agent}`} data-testid={`user-role-badge-${member.id}`}>
                      {member.role}
                    </span>
                  </TableCell>
                  <TableCell className="px-3 py-2 text-sm font-medium text-[#001935]">{member.plan_type || "—"}</TableCell>
                  <TableCell className="px-3 py-2">
                    <span className={`inline-flex rounded-sm border px-2 py-1 text-xs font-medium ${statusClasses[member.subscription_status] || "bg-slate-50 text-slate-700 border-slate-200"}`} data-testid={`user-status-badge-${member.id}`}>
                      {member.subscription_status || "Trial"}
                    </span>
                  </TableCell>
                  <TableCell className="px-3 py-2">
                    <div className="flex items-center justify-end gap-2">
                      <button className="inline-flex h-8 items-center justify-center rounded-sm border border-[#001935]/15 px-3 text-sm font-medium text-[#001935] transition hover:bg-[#001935]/5" data-testid={`user-edit-button-${member.id}`} onClick={() => onEditUser(member.id)} type="button">
                        <PencilLine className="mr-2 h-4 w-4" /> Edit
                      </button>
                      <button className="inline-flex h-8 items-center justify-center rounded-sm border border-red-200 px-3 text-sm font-medium text-red-600 transition hover:bg-red-50" data-testid={`user-delete-button-${member.id}`} onClick={() => onDeleteUser(member.id)} type="button">
                        <Trash2 className="mr-2 h-4 w-4" /> Delete
                      </button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        </>
      )}
    </div>
  );
};